import { useCallback, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import {
  Button,
  ConfigProvider,
  Drawer,
  Dropdown,
  Layout,
  Menu,
  Space,
  Typography,
  theme as antdTheme,
  type MenuProps,
} from 'antd';
import { LogoutOutlined, MenuOutlined, UserOutlined } from '@ant-design/icons';
import { VENDOIQ_PALETTE, chrome } from '@viq-admin/theme';
import { ThemeToggle } from './ThemeToggle';
import { space, tracking, typeScale } from './tokens';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';
import { useAdminContentPadding, useCompactHeader, useNarrowViewport } from './useNarrowViewport';
import { PanelToggle } from './PanelToggle';
import {
  ADMIN_HEADER_HEIGHT,
  PANEL_COLLAPSED_WIDTH,
  PANEL_TRANSITION_MS,
} from './panelLayout';
import './adminLayout.css';

const { Header, Content, Sider } = Layout;

/** Expanded rail width. */
const NAV_WIDTH = 200;
/**
 * The phone menu drawer: wider than the rail so a thumb has room, and so the
 * console's tenant picker (moved in here off the header) fits at its own width.
 */
const NAV_DRAWER_WIDTH = 280;
/**
 * Gap between a nav item's highlight (the gold "you are here" pill, and the
 * hover tint) and the rail's edges. antd's default of 4px left the pill almost
 * touching the rail's right-edge line; 6px gives it room without shrinking the
 * collapsed pill below a comfortable 36px.
 */
const NAV_ITEM_INSET = 6;
const NAV_COLLAPSED_KEY = 'viq-admin.nav.collapsed';
const NAV_TRANSITION = (['width', 'min-width', 'max-width', 'flex-basis'] as const)
  .map((p) => `${p} ${PANEL_TRANSITION_MS}ms ease-in-out`)
  .join(', ');

function readCollapsed(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
  } catch {
    // localStorage throws in private-mode / sandboxed contexts — start expanded.
    return false;
  }
}

function persistCollapsed(next: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(NAV_COLLAPSED_KEY, next ? '1' : '0');
  } catch {
    // Non-fatal: the rail still toggles, the choice just isn't remembered.
  }
}

/**
 * A heading that gathers neighbouring nav items into a section of the IQ
 * Panel (e.g. the console's "Daily" / "Setup" / "AI prompts"). Optional: a
 * shell whose items carry no `group` (the customer portal) gets the flat list
 * exactly as before.
 */
export interface AdminNavGroup {
  /** Stable id for the section; items in the same section share it. */
  key: string;
  /** Heading text, shown small and uppercase above the section's items. */
  label: string;
  /**
   * Fold the section into one expandable row, closed unless the active page
   * is one of its items (the operator can still open it by hand). For a
   * section that's rarely visited and would otherwise crowd the rail.
   */
  collapsible?: boolean;
  /**
   * Icon for a `collapsible` section's folded row. (The collapsed rail has no
   * folds: it shows the section's items as icons, like every other section.)
   */
  icon?: ReactNode;
}

export interface AdminNavItem {
  key: string;
  label: ReactNode;
  href: string;
  /** Optional leading icon (antd icon element) shown before the label. */
  icon?: ReactNode;
  /**
   * Optional section this item belongs to. Consecutive items with the same
   * `group.key` render under one heading, in the order given; pass the same
   * object (or at least the same key) to each item of a section.
   */
  group?: AdminNavGroup;
}

/** The signed-in identity shown in the header account dropdown. */
export interface AdminLayoutUser {
  name?: string;
  email?: string;
  /** Human-readable role label (e.g. "Company Admin", "Super Admin"). */
  roleLabel?: string;
}

export interface AdminLayoutProps {
  /** Brand mark (wordmark img or text) shown at the header left. */
  brand: ReactNode;
  /** Optional badge next to the brand — e.g. a "SUPER-ADMIN" tag identifying the plane. */
  badge?: ReactNode;
  navItems: AdminNavItem[];
  /**
   * Optional renderer for a nav item's clickable element. Defaults to a plain
   * `<a href>` (full navigation). Pass a router-aware renderer (e.g. React
   * Router `<Link>`) for client-side routing without full reloads.
   *
   * `children` is what the link must wrap, and it CHANGES with the rail's
   * state: the label while expanded, the icon once collapsed (antd hides the
   * label there, which would take the link with it). Render `children` rather
   * than reaching for `item.label` directly, or the collapsed rail will look
   * clickable and do nothing.
   */
  renderNavLink?: (item: AdminNavItem, children: ReactNode) => ReactNode;
  /** Active nav key. If omitted, derived from window.location.pathname (longest-prefix match). */
  selectedKey?: string;
  /** Extra header content (e.g. the super-admin "operate-as-tenant" picker). */
  headerExtras?: ReactNode;
  /**
   * A search control that stays in the header at every width — unlike
   * `headerExtras`, which moves into the menu drawer on a phone. Sits right
   * after the brand on a desktop; on a phone it joins the icon buttons at the
   * header's right, so it must render compactly there (the console passes a
   * search box that turns into an icon button on a phone).
   */
  headerSearch?: ReactNode;
  /** Signed-in identity, shown in the right-aligned account dropdown. */
  user?: AdminLayoutUser;
  /** Sign-out handler — invoked from the account dropdown's "Sign out" item. */
  onSignOut?: () => void;
  children: ReactNode;
}

function deriveSelectedKey(navItems: AdminNavItem[]): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const path = window.location.pathname;
  return navItems
    .filter((n) => path === n.href || path.startsWith(`${n.href}/`))
    .sort((a, b) => b.href.length - a.href.length)[0]?.key;
}

type NavMenuItems = NonNullable<MenuProps['items']>;
type NavMenuItem = NavMenuItems[number];

/** Menu key of a section's heading / folded row; namespaced so it can't clash with an item key. */
const sectionKey = (group: AdminNavGroup) => `nav-section:${group.key}`;

/**
 * The nav split into sections: each run of consecutive items sharing a
 * `group.key` is one section; an item with no group stands alone.
 */
function navSections(navItems: AdminNavItem[]): { group?: AdminNavGroup; items: AdminNavItem[] }[] {
  const sections: { group?: AdminNavGroup; items: AdminNavItem[] }[] = [];
  for (const item of navItems) {
    const last = sections[sections.length - 1];
    if (last && item.group && last.group?.key === item.group.key) last.items.push(item);
    else sections.push({ group: item.group, items: [item] });
  }
  return sections;
}

/**
 * Menu items for a grouped nav. A plain section is an antd item group under a
 * small uppercase heading. A `collapsible` section folds into one expandable
 * row — except on the collapsed rail (`iconsOnly`), which has no room for a
 * heading or a fold: there every section is a run of icons, split from the
 * next by a hairline, exactly as the flat rail shows them.
 */
function groupedNavMenuItems(
  navItems: AdminNavItem[],
  renderItem: (item: AdminNavItem) => NavMenuItem,
  iconsOnly: boolean,
): NavMenuItems {
  return navSections(navItems).flatMap(({ group, items }): NavMenuItems => {
    if (!group) return items.map(renderItem);
    if (group.collapsible && !iconsOnly) {
      return [
        {
          key: sectionKey(group),
          label: group.label,
          icon: group.icon,
          children: items.map(renderItem),
        },
      ];
    }
    return [
      {
        type: 'group',
        key: sectionKey(group),
        label: iconsOnly ? (
          <div aria-hidden style={{ height: 1, background: chrome.hoverOnNavy }} />
        ) : (
          <span
            style={{
              color: chrome.onNavyMuted,
              fontSize: typeScale.caption,
              fontWeight: 600,
              letterSpacing: tracking.eyebrow,
              textTransform: 'uppercase',
            }}
          >
            {group.label}
          </span>
        ),
        children: items.map(renderItem),
      },
    ];
  });
}

/**
 * Shared admin chrome (header + sider nav + content), identical layout/styling
 * AND behavior across both SPA shells — the customer panel and the super-admin
 * console. Each shell passes its own brand, badge, nav items, header slot, user
 * identity, and sign-out handler; the look and interactions are shared. See
 * ADR 016.
 *
 * The header is brand-navy in BOTH light and dark (not `colorPrimary`, which is
 * the blue accent in dark mode); the sider menu is `theme="dark"` + transparent
 * so it reads on the navy chrome regardless of app mode. The right side is the
 * theme toggle + an account dropdown (name/role → Sign out).
 *
 * The nav rail ("IQ Panel") collapses to icons only, toggled from the control at
 * its top-left and remembered in localStorage. The two SPAs are separate
 * origins, so each remembers its own rail without any extra scoping.
 *
 * On a phone-width viewport (`useNarrowViewport`) there is no rail: a menu
 * button at the header's left opens the IQ Panel as a drawer, which closes once
 * an item is picked. `headerExtras` moves into that drawer too, the account
 * button drops to its icon (the name and role move into its dropdown), and the
 * page padding tightens. The desktop layout is unchanged. `headerSearch`, by
 * contrast, stays in the header at every width: beside the brand on desktop,
 * and with the right-hand icons once the header is compact (below 992px).
 *
 * Nav items may carry an optional `group` (see `AdminNavGroup`) to split the
 * IQ Panel into headed sections, one of which may fold away. With no groups
 * the panel is the flat list it always was: none of the grouped-only props,
 * tokens or class names below are applied.
 */
export function AdminLayout({
  brand,
  badge,
  navItems,
  renderNavLink,
  selectedKey,
  headerExtras,
  headerSearch,
  user,
  onSignOut,
  children,
}: AdminLayoutProps) {
  const active = selectedKey ?? deriveSelectedKey(navItems);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [menuOpen, setMenuOpen] = useState(false);
  const reducedMotion = usePrefersReducedMotion();
  const narrow = useNarrowViewport();
  // Narrow implies compact; a tablet is compact but keeps the rail.
  const compactHeader = useCompactHeader() || narrow;
  const contentPadding = useAdminContentPadding();
  const { token } = antdTheme.useToken();

  const grouped = navItems.some((n) => n.group);
  // Folded sections stay shut unless they hold the current page. The operator
  // can open or shut one by hand; that holds until the next navigation (or
  // rail toggle), which folds things back to the rule. Folds only exist while
  // labels show (expanded rail, phone drawer), so the collapsed rail has none.
  const activeGroup = navItems.find((n) => n.key === active)?.group;
  const railCollapsed = !narrow && collapsed;
  const activeFold =
    activeGroup?.collapsible && !railCollapsed ? sectionKey(activeGroup) : undefined;
  const foldContext = `${active ?? ''}|${railCollapsed}`;
  const [manualFolds, setManualFolds] = useState<{ context: string; keys: string[] }>();
  // Memoised: antd's Menu caches its open keys in an effect keyed on their
  // identity, so a fresh array every render would re-render it every time.
  const openFolds = useMemo(
    () =>
      manualFolds?.context === foldContext ? manualFolds.keys : activeFold ? [activeFold] : [],
    [manualFolds, foldContext, activeFold],
  );
  const onFoldChange = (keys: string[]) => setManualFolds({ context: foldContext, keys });

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      persistCollapsed(next);
      return next;
    });
  }, []);

  const navLink = (item: AdminNavItem, content: ReactNode) =>
    renderNavLink ? renderNavLink(item, content) : <a href={item.href}>{content}</a>;

  // Puts an expanded item's icon on the same vertical line as the collapsed
  // rail — and the toggle above it — so the icons stay put as the rail
  // collapses rather than sliding sideways. An item's icon sits past its outer
  // inset and the indent; the icon itself is `fontSize` wide.
  const navInlineIndent = PANEL_COLLAPSED_WIDTH / 2 - NAV_ITEM_INSET - token.fontSize / 2;

  const drawerItem = (n: AdminNavItem): NavMenuItem => ({
    key: n.key,
    icon: n.icon,
    label: navLink(n, n.label),
  });
  const railItem = (n: AdminNavItem): NavMenuItem => ({
    key: n.key,
    // The click target follows whatever is actually visible. antd
    // hides the label on a collapsed inline menu, so a link that
    // only ever wrapped the label would leave the icon looking
    // clickable while doing nothing.
    icon: collapsed ? navLink(n, n.icon) : n.icon,
    label: collapsed ? n.label : navLink(n, n.label),
    // Only while collapsed: antd renders `title` as the tooltip for
    // a collapsed item, but also as a native `title` attribute —
    // which on the expanded rail would add a second, slower browser
    // tooltip over every nav item.
    title: collapsed && typeof n.label === 'string' ? n.label : undefined,
  });

  // Grouped-only extras, all inert for a flat nav. A folded section's inner
  // list would otherwise get antd's near-black dark sub-menu background on the
  // navy rail; `openKeys` drives the fold rule above; the class + variable
  // feed the "section holding the current page" style in adminLayout.css.
  const menuTheme = {
    components: {
      Menu: grouped
        ? { itemMarginInline: NAV_ITEM_INSET, darkSubMenuItemBg: 'transparent' }
        : { itemMarginInline: NAV_ITEM_INSET },
    },
  };
  const menuStyle: CSSProperties = {
    flex: 1,
    borderRight: 0,
    background: 'transparent',
    ...(grouped ? ({ '--viq-nav-section-active': chrome.onNavy } as CSSProperties) : {}),
  };
  const groupedMenuProps = grouped
    ? { className: 'viq-nav-menu-grouped', openKeys: openFolds, onOpenChange: onFoldChange }
    : {};

  const accountLabel = user?.name ?? user?.email ?? 'Account';

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          height: ADMIN_HEADER_HEIGHT,
          background: VENDOIQ_PALETTE.navy,
          padding: narrow ? `0 ${space.md}px` : '0 24px',
          display: 'flex',
          alignItems: 'center',
          gap: narrow ? space.sm : space.lg,
        }}
      >
        {narrow && (
          <Button
            type="text"
            icon={<MenuOutlined />}
            aria-label="Open IQ Panel"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
            style={{ color: chrome.onNavy, flexShrink: 0 }}
          />
        )}
        <span
          style={{
            color: chrome.onNavy,
            fontWeight: 700,
            fontSize: 16,
            display: 'flex',
            alignItems: 'center',
            gap: space.md,
            // The brand never gives way: squeezed, a two-part brand (the
            // console's "- Console") wraps onto a second line below the bar.
            // The header search is the item that shrinks instead.
            flexShrink: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {brand}
          {badge}
        </span>
        {!compactHeader && headerSearch}
        {!narrow && headerExtras}
        <Space style={{ marginLeft: 'auto' }} size={narrow ? space.xs : undefined}>
          {compactHeader && headerSearch}
          <ThemeToggle />
          {onSignOut && (
            <Dropdown
              trigger={['click']}
              menu={{
                items: [
                  // No room in a phone or tablet header for who is signed in, so
                  // it heads the menu instead — read-only, not an action.
                  ...(compactHeader
                    ? [
                        {
                          key: 'identity',
                          disabled: true,
                          label: (
                            <Space direction="vertical" size={0}>
                              <Typography.Text strong>{accountLabel}</Typography.Text>
                              {user?.roleLabel && (
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                  {user.roleLabel}
                                </Typography.Text>
                              )}
                            </Space>
                          ),
                        },
                        { type: 'divider' as const },
                      ]
                    : []),
                  { key: 'signout', icon: <LogoutOutlined />, label: 'Sign out', onClick: onSignOut },
                ],
              }}
            >
              {compactHeader ? (
                <Button
                  type="text"
                  icon={<UserOutlined />}
                  aria-label={`Account: ${accountLabel}`}
                  style={{ color: chrome.onNavy }}
                />
              ) : (
                <Button type="text" style={{ color: chrome.onNavy }}>
                  <Space size={6}>
                    <UserOutlined />
                    {accountLabel}
                    {user?.roleLabel && (
                      <Typography.Text style={{ color: chrome.onNavyMuted, fontSize: 12 }}>
                        {user.roleLabel}
                      </Typography.Text>
                    )}
                  </Space>
                </Button>
              )}
            </Dropdown>
          )}
        </Space>
      </Header>
      <Layout>
        {narrow ? (
          <Drawer
            placement="left"
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            width={NAV_DRAWER_WIDTH}
            closable={false}
            rootClassName="viq-nav-drawer"
            styles={{
              body: { padding: 0, display: 'flex', flexDirection: 'column' },
              content: { background: VENDOIQ_PALETTE.navy },
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <PanelToggle
                collapsed={false}
                onToggle={() => setMenuOpen(false)}
                tooltip="Hide IQ Panel"
                tone="navy"
              />
              <span
                aria-hidden
                style={{
                  color: chrome.onNavy,
                  fontSize: typeScale.h5,
                  fontWeight: 600,
                  pointerEvents: 'none',
                }}
              >
                IQ Panel
              </span>
            </div>
            {headerExtras && (
              <div style={{ padding: `${space.xs}px ${space.lg}px ${space.md}px` }}>
                {headerExtras}
              </div>
            )}
            <ConfigProvider theme={menuTheme}>
              <Menu
                mode="inline"
                theme="dark"
                inlineIndent={navInlineIndent}
                selectedKeys={active ? [active] : []}
                // Picking a page is the end of the drawer's job. (Opening a
                // folded section is not a pick: antd fires no click for it.)
                onClick={() => setMenuOpen(false)}
                style={menuStyle}
                {...groupedMenuProps}
                items={
                  grouped
                    ? groupedNavMenuItems(navItems, drawerItem, false)
                    : navItems.map(drawerItem)
                }
              />
            </ConfigProvider>
          </Drawer>
        ) : (
          <Sider
            className="viq-nav-sider"
            width={NAV_WIDTH}
            collapsible
            collapsed={collapsed}
            collapsedWidth={PANEL_COLLAPSED_WIDTH}
            // Our own control sits at the top of the rail instead.
            trigger={null}
            style={{
              transition: reducedMotion ? 'none' : NAV_TRANSITION,
              // The rail's right edge, in both states. Drawn as an inset shadow
              // rather than a border so it takes no width: the collapsed rail
              // stays exactly PANEL_COLLAPSED_WIDTH, keeping its icons and toggle
              // centred. A panel placed beside it (the Batch event rail) shares
              // this line as its left edge instead of drawing a second one.
              boxShadow: `inset -1px 0 0 ${token.colorBorderSecondary}`,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Toggle + panel name, mirroring the Batch event rail's own
                  toggle-then-title row. `overflow: hidden` lets the name slide
                  out of view behind the closing rail instead of wrapping. */}
              <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, overflow: 'hidden' }}>
                <PanelToggle
                  collapsed={collapsed}
                  onToggle={toggleCollapsed}
                  tooltip={collapsed ? 'Show IQ Panel' : 'Hide IQ Panel'}
                  tone="navy"
                />
                <span
                  // Decorative: the toggle beside it already names the panel to a
                  // screen reader, so this would just be read twice.
                  aria-hidden
                  style={{
                    color: chrome.onNavy,
                    fontSize: typeScale.h5,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    // Not a click target — the toggle is.
                    pointerEvents: 'none',
                    opacity: collapsed ? 0 : 1,
                    transition: reducedMotion
                      ? 'none'
                      : `opacity ${PANEL_TRANSITION_MS}ms ease-in-out`,
                  }}
                >
                  IQ Panel
                </span>
              </div>
              {/* Scoped here, next to the indent math that depends on it. Merges
                  with — doesn't replace — the theme's Menu tokens. */}
              <ConfigProvider theme={menuTheme}>
                <Menu
                  mode="inline"
                  theme="dark"
                  inlineIndent={navInlineIndent}
                  selectedKeys={active ? [active] : []}
                  style={menuStyle}
                  {...groupedMenuProps}
                  items={
                    grouped
                      ? groupedNavMenuItems(navItems, railItem, collapsed)
                      : navItems.map(railItem)
                  }
                />
              </ConfigProvider>
            </div>
          </Sider>
        )}
        <Layout style={{ padding: contentPadding }}>
          {/* `minWidth: 0` lets a wide child (a table) scroll inside the page
              instead of stretching it past the viewport. */}
          <Content style={{ minWidth: 0 }}>{children}</Content>
        </Layout>
      </Layout>
    </Layout>
  );
}
